// src/pages/b2b/B2BTeamPage.js - Team 관리 페이지

import React, { useEffect, useState } from 'react';
import B2BLayout from '../../components/b2b/B2BLayout';
import { useAppContext } from '../../context/AppContext';
import { getMyTeam, addTeamMember, removeTeamMember, changeTeamMemberRole } from '../../services/api';
import { usePageHeader } from '../../context/PageHeaderContext';
import './B2BTeamPage.scss';

const B2BTeamPage = () => {
  const { user } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [actionMenuUserId, setActionMenuUserId] = useState(null);
  const [changingOwnerUserId, setChangingOwnerUserId] = useState(null);

  const loadTeam = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getMyTeam();
      if (resp.success) {
        setTeam(resp.team);
        setMembers(resp.members || []);
      } else {
        setError(resp.error || '팀 정보를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('팀 정보 로드 실패:', err);
      setError(err.message || '팀 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeam();
  }, []);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!newMemberEmail.trim()) return;

    setAdding(true);
    try {
      // 현재는 이미 가입된 이메일을 대상으로 즉시 팀원으로 추가하지만,
      // UX 상으로는 "초대 메일 발송" 느낌으로 안내
      const resp = await addTeamMember(newMemberEmail.trim());
      if (resp.success) {
        alert('초대 메일을 발송했습니다. (현재는 이미 가입된 계정은 바로 팀원으로 추가됩니다)');
        await loadTeam();
        setNewMemberEmail('');
        setShowInviteModal(false);
      } else {
        alert(resp.error || '팀원 초대에 실패했습니다.');
      }
    } catch (err) {
      console.error('팀원 초대 실패:', err);
      alert(err.message || '팀원 초대 중 오류가 발생했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('이 계정을 정말 삭제하시겠습니까?\n해당 계정과 계정이 생성한 산출물이 모두 삭제됩니다.')) return;
    setRemovingUserId(userId);
    try {
      const resp = await removeTeamMember(userId);
      if (resp.success) {
        setMembers(prev => prev.filter(m => m.user_id !== userId));
      } else {
        alert(resp.error || '팀원 제거에 실패했습니다.');
      }
    } catch (err) {
      console.error('팀원 제거 실패:', err);
      alert(err.message || '팀원 제거 중 오류가 발생했습니다.');
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleChangeOwner = async (userId) => {
    if (!window.confirm('이 팀원을 소유자로 변경하시겠습니까?\n현재 소유자는 팀원으로 변경됩니다.')) return;
    setChangingOwnerUserId(userId);
    setActionMenuUserId(null);
    try {
      const resp = await changeTeamMemberRole(userId, 'owner');
      if (resp.success) {
        await loadTeam();
      } else {
        alert(resp.error || '권한 변경에 실패했습니다.');
      }
    } catch (err) {
      console.error('권한 변경 실패:', err);
      alert(err.message || '권한 변경 중 오류가 발생했습니다.');
    } finally {
      setChangingOwnerUserId(null);
    }
  };

  usePageHeader({
    title: 'Team 관리',
    subtitle: '엔터프라이즈 팀의 멤버를 관리하고 권한을 설정하세요.',
    showBack: false,
  });

  return (
    <B2BLayout hideCollapseButton={false}>
      <div className="b2b-team-page">

        <div className="b2b-page-content">
          {loading ? (
            <div className="b2b-placeholder">
              <p>팀 정보를 불러오는 중...</p>
            </div>
          ) : error ? (
            <div className="b2b-placeholder">
              <p style={{ color: '#b91c1c' }}>{error}</p>
            </div>
          ) : !team ? (
            <div className="b2b-placeholder">
              <p>연결된 팀 정보가 없습니다. Admin에게 문의해 엔터프라이즈 팀을 설정해주세요.</p>
            </div>
          ) : (
            <>
              {/* 팀 정보 요약 + 초대 버튼 */}
              <div className="team-summary">
                <div>
                  <h2>{team.name}</h2>
                  {team.company_name && <p className="team-company-name" style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0 0' }}>{team.company_name}</p>}
                  {team.description && <p className="team-description">{team.description}</p>}
                  <span className="team-members-count">팀원 {members.length}명</span>
                </div>
                <div className="team-meta">
                  <button
                    type="button"
                    className="team-invite-open-button"
                    onClick={() => setShowInviteModal(true)}
                  >
                    팀원 초대하기
                  </button>
                </div>
              </div>

              {/* 팀원 테이블 */}
              <div className="team-table-wrapper">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>계정</th>
                      <th>권한</th>
                      <th>액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="team-empty">
                          팀원이 없습니다.
                        </td>
                      </tr>
                    ) : (
                      members.map((m, idx) => {
                        const isOwner = m.role === 'owner';
                        const isCurrentUserOwner = user && team && String(user.id) === String(team.owner_id);
                        return (
                          <tr key={m.user_id}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="member-account">
                                <div className="member-email">{m.email || '(이메일 없음)'}</div>
                                {m.name && <div className="member-name">{m.name}</div>}
                              </div>
                            </td>
                            <td>
                              <span className={`member-role-badge ${isOwner ? 'owner' : 'member'}`}>
                                {isOwner ? '소유자' : '팀원'}
                              </span>
                            </td>
                            <td>
                              <div className="member-actions">
                                <div className="member-action-menu-trigger">
                                  <button
                                    type="button"
                                    className="member-action-button secondary"
                                    title={isCurrentUserOwner && !isOwner ? '권한 변경, 계정 삭제' : '소유자만 사용할 수 있습니다'}
                                    disabled={!isCurrentUserOwner || isOwner}
                                    onClick={() => {
                                      if (!isCurrentUserOwner || isOwner) return;
                                      setActionMenuUserId(prev => prev === m.user_id ? null : m.user_id);
                                    }}
                                  >
                                    {changingOwnerUserId === m.user_id || removingUserId === m.user_id ? '...' : '⋯'}
                                  </button>
                                  {isCurrentUserOwner && actionMenuUserId === m.user_id && (
                                    <div className="member-action-menu">
                                      <button
                                        type="button"
                                        onClick={() => handleChangeOwner(m.user_id)}
                                      >
                                        권한 변경하기 (소유자로)
                                      </button>
                                      <button
                                        type="button"
                                        className="danger"
                                        onClick={() => handleRemoveMember(m.user_id)}
                                      >
                                        계정 삭제
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {/* 팀원 초대 모달 */}
              {showInviteModal && (
                <div className="team-modal-backdrop">
                  <div className="team-modal" onClick={(e) => e.stopPropagation()}>
                    <h3>팀원 초대</h3>
                    <p className="team-add-hint">
                      초대할 사용자의 이메일을 입력해주세요. 현재는 이미 가입된 ReOpsAI 계정만 즉시 팀원으로 추가됩니다.
                    </p>
                    <form onSubmit={handleSendInvite}>
                      <div className="team-add-row">
                        <input
                          type="email"
                          placeholder="user@example.com"
                          value={newMemberEmail}
                          onChange={(e) => setNewMemberEmail(e.target.value)}
                        />
                      </div>
                      <div className="team-modal-actions">
                        <button
                          type="button"
                          className="member-action-button secondary"
                          onClick={() => !adding && setShowInviteModal(false)}
                        >
                          취소
                        </button>
                        <button
                          type="submit"
                          className="member-action-button"
                          disabled={adding || !newMemberEmail.trim()}
                          style={{ backgroundColor: '#6366f1', color: '#fff' }}
                        >
                          {adding ? '초대 중...' : '초대 메일 발송'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </B2BLayout>
  );
};

export default B2BTeamPage;
